import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Send, Loader2, Star, Lock, Heart, Mail, Vote, Award, Megaphone, UtensilsCrossed, Calendar, Camera, X, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PATREON_URL = process.env.REACT_APP_PATREON_URL || "https://patreon.com/ConceptAndFormality";

const FREE_PERKS = [
  { icon: Calendar, label: "Show Updates & Announcements" },
  { icon: Camera, label: "Public Behind-the-Scenes" },
  { icon: Mail, label: "First-Look Email Drops" },
];

const VIP_PERKS = [
  { icon: Award, label: "Name on the C.A.F. Supporter Wall" },
  { icon: Megaphone, label: "Live Shoutout at Shows" },
  { icon: UtensilsCrossed, label: "Free Food + Drinks at Shows" },
  { icon: Vote, label: "Vote on Show Menu + Setlist" },
];

// Shared signup form used by both tiers
function SignupForm({ tier, ctaLabel, ctaIcon: Icon, onSuccess, accentColor }) {
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Email is required.");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mosh-team`, {
        email: email.trim(),
        instagram: instagram.trim() || null,
        tier,
      });
      onSuccess(r.data);
      setEmail("");
      setInstagram("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const isVip = tier === "vip";
  const focusBorder = isVip ? "focus:border-[#FF00E5]" : "focus:border-[#00F0FF]";

  return (
    <form onSubmit={submit} data-testid={`mosh-form-${tier}`} className="space-y-4">
      <input
        data-testid={`mosh-input-email-${tier}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className={`w-full bg-black/40 border-b-2 border-white/15 ${focusBorder} outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors text-sm`}
      />
      <input
        data-testid={`mosh-input-instagram-${tier}`}
        type="text"
        value={instagram}
        onChange={(e) => setInstagram(e.target.value)}
        placeholder="@instagram (optional)"
        className={`w-full bg-black/40 border-b-2 border-white/15 ${focusBorder} outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors text-sm`}
      />
      <button
        data-testid={`mosh-submit-${tier}`}
        type="submit"
        disabled={loading}
        className={`btn-shine w-full px-6 py-3.5 font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 transition-all flex items-center justify-center gap-2 ${
          isVip
            ? "bg-gradient-to-r from-[#FF00E5] via-[#a900b8] to-[#FF00E5] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white glow-magenta"
            : "bg-transparent border-2 border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:glow-cyan"
        }`}
        style={accentColor ? { color: accentColor } : {}}
      >
        {loading ? <><Loader2 size={14} className="animate-spin" /> {isVip ? "Processing…" : "Enlisting…"}</> : <><Icon size={13} /> {ctaLabel}</>}
      </button>
    </form>
  );
}

function FreeTier({ onJoined }) {
  return (
    <div data-testid="mosh-free-tier" className="glass-card p-7 md:p-9 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Tier 01</span>
          <h3 className="font-display font-black text-2xl md:text-3xl text-cream-soft mt-1">STREET PASS</h3>
        </div>
        <span className="font-display font-black text-3xl md:text-4xl text-gradient-neon">FREE</span>
      </div>
      <p className="text-white/55 text-sm">For anyone in the C.A.F. orbit. Stay in the loop.</p>

      <ul className="mt-5 space-y-2.5 flex-1">
        {FREE_PERKS.map((p) => (
          <li key={p.label} className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 border border-[#00F0FF]/30 bg-black/50 shrink-0">
              <p.icon size={13} className="text-[#00F0FF]" />
            </span>
            <span className="font-mono-display text-[11px] uppercase tracking-[0.15em] text-white/75">{p.label}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <SignupForm
          tier="free"
          ctaLabel="Enlist Free"
          ctaIcon={Send}
          onSuccess={() => onJoined("free")}
        />
      </div>
    </div>
  );
}

function VipTier({ onJoined }) {
  return (
    <div data-testid="mosh-vip-tier" className="glass-card relative p-7 md:p-9 h-full flex flex-col border-[#FF00E5]/40 hover:border-[#FF00E5]/80 overflow-hidden">
      <div className="absolute -top-32 -right-20 w-72 h-72 bg-[#FF00E5]/20 blur-[100px] pointer-events-none" aria-hidden="true" />

      <div className="absolute top-4 right-4 z-10">
        <span className="px-3 py-1 bg-[#FF00E5] text-black font-mono-display text-[9px] uppercase tracking-[0.25em] font-bold flex items-center gap-1">
          <Star size={10} className="fill-black" /> Most Popular
        </span>
      </div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="mb-1">
          <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#FF00E5]">Tier 02</span>
          <h3 className="font-display font-black text-2xl md:text-3xl text-cream-soft mt-1">
            MOSH TEAM <span className="text-[#FF00E5] text-glow-magenta">VIP</span>
          </h3>
        </div>

        <div className="flex items-baseline gap-1 mb-3">
          <span className="font-display font-black text-4xl md:text-5xl text-gradient-neon">$10</span>
          <span className="font-mono-display text-xs uppercase tracking-[0.2em] text-white/55">/ month</span>
        </div>
        <p className="text-white/65 text-sm">For the inner circle. Tangible perks, IRL access, real influence.</p>

        <ul className="mt-5 space-y-2.5 flex-1">
          {VIP_PERKS.map((p) => (
            <li key={p.label} className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 border border-[#FF00E5]/40 bg-black/50 shrink-0">
                <p.icon size={13} className="text-[#FF00E5]" />
              </span>
              <span className="font-mono-display text-[11px] uppercase tracking-[0.15em] text-white/85">{p.label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6">
          <SignupForm
            tier="vip"
            ctaLabel="Continue To Patreon"
            ctaIcon={Lock}
            onSuccess={() => onJoined("vip")}
          />
          <p className="mt-3 text-[10px] font-mono-display uppercase tracking-[0.2em] text-white/40 text-center">
            Step 1 of 2 &middot; You&apos;ll finish on Patreon
          </p>
        </div>
      </div>
    </div>
  );
}

function ThankYouModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="mosh-thank-you-modal"
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up"
      onClick={onClose}
    >
      <button
        data-testid="thank-you-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center border border-white/30 bg-black/60 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:glow-cyan transition-all"
      >
        <X size={18} />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-lg w-full glass-card p-10 md:p-12 text-center border-[#00F0FF]/40 glow-cyan"
      >
        {/* Decorative glow blobs */}
        <div className="absolute -top-20 -left-20 w-60 h-60 bg-[#00F0FF]/20 blur-[80px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#FF00E5]/20 blur-[80px] pointer-events-none" aria-hidden="true" />

        <div className="relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#00F0FF] bg-black/60 glow-cyan mb-6">
            <CheckCircle2 size={32} className="text-[#00F0FF]" />
          </div>

          <h3 className="font-display font-black text-3xl md:text-4xl text-cream-soft leading-tight">
            YOU&apos;RE IN.
          </h3>
          <p className="mt-3 font-mono-display text-xs uppercase tracking-[0.3em] text-[#00F0FF]">
            Welcome to the Mosh Team
          </p>

          <p className="mt-5 text-white/70 text-sm md:text-base leading-relaxed">
            Watch your inbox for the first transmission — show updates, behind-the-scenes content, and first-look album news land there first.
          </p>

          <button
            data-testid="thank-you-confirm"
            onClick={onClose}
            className="btn-shine mt-7 px-8 py-3.5 bg-transparent border-2 border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 font-display font-bold text-xs uppercase tracking-[0.3em] hover:glow-cyan transition-all"
          >
            Back to the Experience
          </button>
        </div>
      </div>
    </div>
  );
}

function VipRedirectModal({ open }) {
  if (!open) return null;
  return (
    <div
      data-testid="mosh-vip-redirect-modal"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="relative max-w-lg w-full glass-card p-10 md:p-12 text-center border-[#FF00E5]/40 glow-magenta">
        <div className="absolute -top-20 -left-20 w-60 h-60 bg-[#FF00E5]/30 blur-[80px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#00F0FF]/20 blur-[80px] pointer-events-none" aria-hidden="true" />

        <div className="relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#FF00E5] bg-black/60 glow-magenta mb-6">
            <Crown size={32} className="text-[#FF00E5]" />
          </div>

          <h3 className="font-display font-black text-3xl md:text-4xl text-cream-soft leading-tight">
            REDIRECTING TO PATREON…
          </h3>
          <p className="mt-3 font-mono-display text-xs uppercase tracking-[0.3em] text-[#FF00E5]">
            Step 2 of 2 · Complete Subscription
          </p>

          <p className="mt-5 text-white/70 text-sm md:text-base leading-relaxed">
            We&apos;ve saved your spot. Finish the $10/mo subscription on Patreon and you&apos;ll unlock all VIP perks instantly.
          </p>

          <div className="mt-6 flex items-center justify-center gap-3">
            <Loader2 size={16} className="animate-spin text-[#FF00E5]" />
            <span className="font-mono-display text-xs uppercase tracking-[0.25em] text-white/55">
              Opening Patreon checkout…
            </span>
          </div>

          <a
            data-testid="vip-redirect-fallback"
            href={PATREON_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 font-mono-display text-[11px] uppercase tracking-[0.25em] text-[#FF00E5] hover:text-glow-magenta"
          >
            Not redirecting? Click here <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function MoshTeam() {
  const [showThankYou, setShowThankYou] = useState(false);
  const [showVipRedirect, setShowVipRedirect] = useState(false);

  const onJoined = (tier) => {
    if (tier === "vip") {
      setShowVipRedirect(true);
      // Brief moment so the user sees the confirmation, then redirect
      setTimeout(() => {
        window.location.href = PATREON_URL;
      }, 1500);
    } else {
      setShowThankYou(true);
    }
  };

  return (
    <section id="mosh" data-testid="mosh-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-7xl mx-auto">
      <div className="absolute -top-20 left-1/4 w-80 h-80 rounded-full bg-[#FF00E5]/10 blur-[100px] pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-20 right-1/4 w-80 h-80 rounded-full bg-[#00F0FF]/10 blur-[100px] pointer-events-none" aria-hidden="true" />

      <div className="relative text-center mb-12 md:mb-16 reveal">
        <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#FF00E5]">
          02 — Fan Club
        </span>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 leading-[0.95]">
          JOIN THE <span className="text-gradient-neon">MOSH TEAM</span>
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-white/65 text-sm md:text-base">
          Two ways in. Both bring you closer to the project.
        </p>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7 max-w-4xl mx-auto">
        <div className="reveal"><FreeTier onJoined={onJoined} /></div>
        <div className="reveal" style={{ transitionDelay: "100ms" }}><VipTier onJoined={onJoined} /></div>
      </div>

      <ThankYouModal open={showThankYou} onClose={() => setShowThankYou(false)} />
      <VipRedirectModal open={showVipRedirect} />
    </section>
  );
}
